import { useRCAMainStyles } from "../RCA.styles";
import { Divider } from "@mui/material";
import { MainTable } from "components/MainTable";
import { useStyles } from "./RCALibrary.styles";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import useRCALibraryColumns from "./components/useRCALibraryColumns";
import { useTableOperations } from "components/MainTable/hooks/useTableOperations";
import { IRCA, IRCALibraryTableOperationFilters } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import { CORPORATE_BACKOFFICE_PATHS, RCA_STATUSES } from "constants/constants";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import RCALibraryFilters from "./components/RCALibraryFilters";
import CreateEditManualRCAModal from "./components/RCALibraryDetail/components/CreateEditManualRCAModal/CreateEditManualRCAModal";
import { RCALibraryFiltersContext } from "contexts/RCALibraryFiltersContext/RCALibraryFiltersContext";
import { RCALibraryFiltersContextProvider } from "contexts/RCALibraryFiltersContext/RCALibraryFiltersContextProvider";

const newRCAOrderList = (rcaList: IRCA[]): IRCA[] => {
    const newRCAList = [...rcaList];
    const newListOrdererByWorstToBestStatus = newRCAList
        .filter((rca) => rca.status.status_id !== RCA_STATUSES.COMPLETED)
        .sort((a, b) => {
            return a.status.order - b.status.order;
        });
    const dueDateRCAList = newListOrdererByWorstToBestStatus.filter((rca) => {
        return moment(rca.due_date).isBefore(moment());
    });
    const notDueDateRCAList = newListOrdererByWorstToBestStatus.filter((rca) => {
        return moment(rca.due_date).isAfter(moment());
    });

    const completedRCAList = newRCAList
        .filter((rca) => rca.status.status_id === RCA_STATUSES.COMPLETED)
        .sort((a, b) => {
            return a.status.order - b.status.order;
        });

    const orderedDueDateRCAList = dueDateRCAList.sort((a, b) => {
        const aDueDate = moment(a.due_date);
        const bDueDate = moment(b.due_date);
        const aDateHeld = a.date_held ? moment(a.date_held) : null;
        const bDateHeld = b.date_held ? moment(b.date_held) : null;
        if (aDateHeld === null && bDateHeld !== null) return -1;
        if (aDateHeld !== null && bDateHeld === null) return 1;
        if (aDueDate.isBefore(bDueDate)) return -1;
        if (aDueDate.isAfter(bDueDate)) return 1;
        return 0;
    });
    const newListOrdererByOldDueDate = [...orderedDueDateRCAList, ...notDueDateRCAList, ...completedRCAList];
    return newListOrdererByOldDueDate;
};

const RCALibraryContent = () => {
    const mainClasses = useRCAMainStyles();
    const classes = useStyles();
    const [sourceRcaList, setSourceRcaList] = useState<"initial" | "hasura">("initial");
    const prevFilterRef = useRef<IRCALibraryTableOperationFilters | undefined>();
    const { handleRequestSort, handleChangeRowsPerPage, handleChangePage, order, orderBy, page, rowsPerPage } =
        useTableOperations();
    const { filters } = useContext(RCALibraryFiltersContext);
    const [isCreateRCAModalOpen, setIsCreateRCAModalOpen] = useState<boolean>(false);

    const queryTableOperationFilters = useMemo(
        () => ({
            order,
            orderBy,
            limit: rowsPerPage,
            offset: page * rowsPerPage,
        }),
        [order, orderBy, rowsPerPage, page],
    );

    const { rcaLibrary, refetch, loading, apolloQueryError, totalCount } = RCALibraryEntity.GetRCALibrary(
        queryTableOperationFilters,
        filters,
    );

    const tableColumns = useRCALibraryColumns(refetch) as unknown as IMainTableColumn[];
    const navigate = useNavigate();

    useEffect(() => {
        if (!prevFilterRef.current) {
            prevFilterRef.current = queryTableOperationFilters;
        }
        if (prevFilterRef.current !== queryTableOperationFilters) {
            prevFilterRef.current = queryTableOperationFilters;
            setSourceRcaList("hasura");
        }
    }, [queryTableOperationFilters, sourceRcaList]);

    const initialRCAOrderedData = useMemo(() => {
        const sortedRCAList = newRCAOrderList(rcaLibrary);

        const sortedRCAListWithExpandedData = sortedRCAList.map((rca) => ({
            ...rca,
            expandedData: rca.action_plans ?? [],
        }));
        return sortedRCAListWithExpandedData;
    }, [rcaLibrary]);

    const hasuraRCAOrderedData = useMemo(() => {
        const newRCAList = rcaLibrary.map((rca) => ({
            ...rca,
            expandedData: rca.action_plans ?? [],
        }));
        return newRCAList;
    }, [rcaLibrary]);

    const rcaDataSource = useMemo(
        () => (sourceRcaList === "initial" ? initialRCAOrderedData : hasuraRCAOrderedData),
        [sourceRcaList, initialRCAOrderedData, hasuraRCAOrderedData],
    );

    return (
        <div className={mainClasses.mainWrapper}>
            <div className={mainClasses.contentHeader}>
                <RCALibraryFilters onClickAddNewRCA={() => setIsCreateRCAModalOpen(true)} />
            </div>
            <div className={mainClasses.contentWrapper}>
                <div className={`${mainClasses.contentCard} ${classes.cardWrapper}`}>
                    <Divider />
                    <MainTable
                        maxHeight={false}
                        customStyle={`${mainClasses.contentTable}`}
                        paperElevation={0}
                        paperCustomStyle={mainClasses.contentTablePaper}
                        columns={tableColumns}
                        tableCellCustomStyle={mainClasses.contentTableCell}
                        customStyleTypography={{ width: "100%" }}
                        tableExpandedRowCustomStyle={mainClasses.contentTableExpandedRow}
                        rows={!loading && !apolloQueryError ? rcaDataSource : []}
                        loading={loading}
                        tableOperationsValues={{ order, orderBy, page, rowsPerPage }}
                        handleRequestSortParent={handleRequestSort}
                        handleChangeRowsPerPageParent={handleChangeRowsPerPage}
                        handleChangePageParent={handleChangePage}
                        pagination
                        count={totalCount}
                        querySorted={!!orderBy}
                        onRowClick={(row: { rca_id?: number }) => {
                            navigate(
                                `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}/${row.rca_id}`,
                                {
                                    state: {
                                        from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}`,
                                    },
                                },
                            );
                        }}
                        onExpandedRowClick={(row: { action_plan_id?: number }) => {
                            navigate(
                                `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_ACTION_PLANS.ROOT}/${row.action_plan_id}`,
                                {
                                    state: {
                                        from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}`,
                                    },
                                },
                            );
                        }}
                    />
                </div>
            </div>
            <CreateEditManualRCAModal
                open={isCreateRCAModalOpen}
                onClose={() => setIsCreateRCAModalOpen(false)}
                onAddRCA={async (rca) => {
                    navigate(
                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}/${rca.rca_id}`,
                        {
                            state: {
                                from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}`,
                            },
                        },
                    );
                    setIsCreateRCAModalOpen(false);
                }}
            />
        </div>
    );
};

const RCALibrary = () => {
    return (
        <RCALibraryFiltersContextProvider>
            <RCALibraryContent />
        </RCALibraryFiltersContextProvider>
    );
};

export default RCALibrary;
